import { useEffect, useRef, useState } from "react";
import { api } from "./workspaceApi";

/** Заявка клиента — как её отдаёт сервер для раздела «Заявки». */
export type OrderRequest = {
  id: string; number: number | null; projectName: string; projectNumber?: string; name: string; phone: string; email: string; comment: string;
  status: "pending" | "approved" | "rejected"; createdAt: string; telegramDelivered: boolean;
  /** Один браузер клиента — общий лимит визуализаций на все его заявки. */
  clientKey?: string;
  quota: { freeLimit: number; bonusLimit: number; manualBonus: number; used: number; total: number; remaining: number } | null;
  files: { clientPdf: boolean; assemblyPdf: boolean; visualization: boolean };
};
type Filter = OrderRequest["status"] | "all";
const FILTERS: [Filter, string][] = [["pending", "Ожидают"], ["approved", "Подтверждены"], ["rejected", "Отклонены"], ["all", "Все"]];
const STATUS: Record<OrderRequest["status"], string> = { pending: "Ожидает проверки", approved: "Подтверждена", rejected: "Отклонена" };
const FILES: [keyof OrderRequest["files"], string][] = [["clientPdf", "PDF проекта"], ["assemblyPdf", "Техническая карта"], ["visualization", "AI-визуализация"]];
const when = (value: string) => new Date(value).toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
const REFRESH_MS = 20_000;

/**
 * Заявки клиентов в кабинете (08.10; раньше — отдельная панель менеджера со
 * своим паролем). Видят администраторы и сотрудники с правом «Заявки».
 */
export function OrdersAdmin({ busy, run, notify, onPending }: {
  busy: boolean;
  run: (action: () => Promise<void>) => Promise<void>;
  notify: (message: string) => void;
  onPending?: (count: number) => void;
}) {
  const [requests, setRequests] = useState<OrderRequest[] | null>(null);
  const [filter, setFilter] = useState<Filter>("pending");
  const [search, setSearch] = useState("");
  const [loadError, setLoadError] = useState("");
  const pendingRef = useRef(onPending);
  pendingRef.current = onPending;
  const load = async () => {
    const list = (await api<{ requests: OrderRequest[] }>("/orders")).requests;
    setRequests(list); setLoadError("");
    pendingRef.current?.(list.filter((item) => item.status === "pending").length);
  };
  useEffect(() => {
    void run(load);
    // Новые заявки появляются сами, пока раздел открыт.
    const timer = window.setInterval(() => { load().catch((error: unknown) => setLoadError(error instanceof Error ? error.message : "Не удалось обновить заявки")); }, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);
  const setStatus = (item: OrderRequest, action: "approve" | "reject") => run(async () => {
    if (action === "reject" && !window.confirm(`Отклонить заявку${item.number ? ` № ${item.number}` : ""} «${item.projectName}» от ${item.name}?`)) return;
    await api(`/orders/${encodeURIComponent(item.id)}/${action}`, "POST", {});
    await load();
    notify(action === "approve" ? `Заявка подтверждена: ${item.name} получил ещё визуализации` : "Заявка отклонена");
  });
  const changeQuota = (item: OrderRequest, action: "grant-1" | "grant-3" | "revoke-1" | "reset") => run(async () => {
    if (action === "reset" && !window.confirm("Обнулить использованные генерации этого клиента? Начисленные сохранятся.")) return;
    const { quota } = await api<{ quota: OrderRequest["quota"] }>(`/orders/${encodeURIComponent(item.id)}/quota/${action}`, "POST", {});
    // Лимит общий на браузер клиента: обновляем все его заявки сразу. Раньше
    // остальные карточки показывали старое число, и «+3» на другой из них
    // выглядел как скачок 6 → 15 (приёмка 12.10).
    setRequests((list) => list?.map((other) => other.id === item.id || (item.clientKey && other.clientKey === item.clientKey)
      ? { ...other, quota } : other) ?? null);
    notify(action === "reset" ? "Использованные генерации обнулены, дневной лимит адреса снят"
      : action === "revoke-1" ? "У клиента на 1 генерацию меньше"
        : `Клиенту добавлено ${action === "grant-1" ? "1 генерация" : "3 генерации"}`);
  });
  /** Телефон и почта копируются щелчком (11.10, adm-02): ссылки tel:/mailto: на компьютере открывали пустой браузер. */
  const copy = (value: string, what: string) => {
    const done = () => notify(`${what} скопирован: ${value}`);
    const fallback = () => {
      const field = document.createElement("textarea");
      field.value = value; field.setAttribute("readonly", ""); field.style.position = "fixed"; field.style.opacity = "0";
      document.body.append(field); field.select();
      try { document.execCommand("copy"); done(); } catch { notify("Не удалось скопировать — выделите текст вручную"); }
      field.remove();
    };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(value).then(done, fallback);
    else fallback();
  };
  const query = search.trim().toLowerCase();
  const shown = (requests ?? []).filter((item) => (filter === "all" || item.status === filter) &&
    (!query || `${item.number ?? ""} №${item.number ?? ""} ${item.projectName} ${item.projectNumber ?? ""} ${item.name} ${item.phone} ${item.email} ${item.comment}`.toLowerCase().includes(query)));
  const count = (status: OrderRequest["status"]) => (requests ?? []).filter((item) => item.status === status).length;
  return <section className="orders-admin">
    <div className="orders-admin__summary">
      {(["pending", "approved", "rejected"] as const).map((status) => <div key={status} className={`is-${status}`}>
        <strong>{requests ? count(status) : "—"}</strong><span>{FILTERS.find(([value]) => value === status)![1]}</span></div>)}
    </div>
    <div className="workspace-library-bar">
      <input aria-label="Поиск заявок" placeholder="Номер, проект, клиент, телефон…" value={search} onChange={(event) => setSearch(event.target.value)} />
      <button type="button" disabled={busy} onClick={() => void run(load)}>Обновить</button>
    </div>
    <div className="catalog-admin__filters" role="group" aria-label="Какие заявки показать">
      {FILTERS.map(([value, title]) => <button key={value} type="button" aria-pressed={filter === value} onClick={() => setFilter(value)}>
        {title}{value !== "all" && requests ? ` · ${count(value)}` : ""}</button>)}
    </div>
    {loadError && <p className="workspace-muted" role="status">{loadError}. Повторю через {REFRESH_MS / 1000} с.</p>}
    {requests === null ? <p role="status">Загружаю заявки…</p> : !shown.length
      ? <div className="workspace-empty"><h3>{filter === "pending" && !query ? "Новых заявок нет" : "Заявок не найдено"}</h3>
        <p>{filter === "pending" && !query ? "Новые заявки появятся здесь сами, страницу обновлять не нужно." : "Измените фильтр или поиск."}</p></div>
      : <div className="orders-admin__list">{shown.map((item) => <article key={item.id} className={`orders-admin__card is-${item.status}`}>
        <div className="orders-admin__project">
          <span className="workspace-eyebrow">{item.number ? `ЗАЯВКА № ${item.number}` : "ЗАЯВКА"} · {when(item.createdAt)}</span>
          <h3>{item.projectName}{item.projectNumber && !item.projectName.includes(item.projectNumber) ? ` (${item.projectNumber})` : ""}</h3>
          <small className={item.telegramDelivered ? "" : "is-warning"}>{item.telegramDelivered ? "Уведомление ушло в Telegram" : "Telegram не подтвердил уведомление"}</small>
        </div>
        <div className="orders-admin__contact">
          <strong>{item.name}</strong>
          <button type="button" className="orders-admin__copy" title="Скопировать телефон" onClick={() => copy(item.phone, "Телефон")}>{item.phone}</button>
          {item.email && <button type="button" className="orders-admin__copy" title="Скопировать адрес" onClick={() => copy(item.email, "Адрес")}>{item.email}</button>}
          {item.comment && <p>{item.comment}</p>}
        </div>
        <div className="orders-admin__actions">
          <span className={`orders-admin__status is-${item.status}`}>{STATUS[item.status]}</span>
          {item.status === "pending" && <>
            <button type="button" className="workspace-primary" disabled={busy} onClick={() => void setStatus(item, "approve")}>Открыть 3 рендера</button>
            <button type="button" className="is-danger" disabled={busy} onClick={() => void setStatus(item, "reject")}>Отклонить</button>
          </>}
          {item.status === "approved" && (item.quota ? <div className="orders-admin__quota">
            <strong>{item.quota.remaining} из {item.quota.total} доступно</strong>
            <small>Использовано: {item.quota.used} · добавлено вручную: {item.quota.manualBonus}</small>
            {(() => {
              const shared = (requests ?? []).filter((other) => other.id !== item.id && item.clientKey && other.clientKey === item.clientKey);
              return shared.length > 0 && <small className="orders-admin__shared">Общий лимит с {shared.length === 1 ? "заявкой" : "заявками"} {shared.map((other) => other.number ? `№ ${other.number}` : "без номера").join(", ")} — клиент отправлял их из одного браузера.</small>;
            })()}
            <div>
              <button type="button" disabled={busy} onClick={() => void changeQuota(item, "grant-1")}>+1</button>
              <button type="button" disabled={busy} onClick={() => void changeQuota(item, "grant-3")}>+3</button>
              <button type="button" disabled={busy || item.quota.remaining <= 0} title="Убрать одну доступную генерацию" onClick={() => void changeQuota(item, "revoke-1")}>−1</button>
              <button type="button" disabled={busy} onClick={() => void changeQuota(item, "reset")}>Сбросить использованные</button>
            </div>
          </div> : <small>Не удалось получить лимит визуализаций</small>)}
        </div>
        <div className="orders-admin__files">
          {FILES.filter(([key]) => item.files[key]).map(([key, title]) =>
            <a key={key} href={`/api/workspace/orders/${encodeURIComponent(item.id)}/files/${key}`} target="_blank" rel="noopener">{title}</a>)}
        </div>
      </article>)}</div>}
  </section>;
}
